// frontend-react/src/services/legaltechRuleService.ts
import { getApiBaseUrl } from '../config/api';
import {
  LegaltechRule,
  LegaltechRuleCreate,
  LegaltechRuleUpdate,
  CsvUploadResult,
  NormalizeResponse,
  BatchNormalizeResponse,
} from '../types/legaltechRule';

const getAuthToken = (): string | null => {
  try {
    return localStorage.getItem('auth_token');
  } catch {
    return null;
  }
};

const authHeaders = (): Record<string, string> => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const BASE = () => `${getApiBaseUrl()}/api/legaltech-rules`;

/** 取得所有律果規則 */
export const listLegaltechRules = async (params?: {
  tag?: string;
  contract_type?: string;
  selection_mode?: string;
  risk_level?: string;
  q?: string;
  include_inactive?: boolean;
}): Promise<LegaltechRule[]> => {
  const url = new URL(BASE(), window.location.origin);
  if (params?.tag) url.searchParams.set('tag', params.tag);
  if (params?.contract_type) url.searchParams.set('contract_type', params.contract_type);
  if (params?.selection_mode) url.searchParams.set('selection_mode', params.selection_mode);
  if (params?.risk_level) url.searchParams.set('risk_level', params.risk_level);
  if (params?.q) url.searchParams.set('q', params.q);
  if (params?.include_inactive) url.searchParams.set('include_inactive', 'true');

  const res = await fetch(url.toString(), { headers: authHeaders() });
  if (!res.ok) throw new Error(`載入律果規則失敗: ${res.status}`);
  return res.json();
};

/** 取得所有適用標籤清單 */
export const listLegaltechTags = async (): Promise<string[]> => {
  const res = await fetch(`${BASE()}/categories`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`載入標籤失敗: ${res.status}`);
  return res.json();
};

/** 新增單條規則 */
export const createLegaltechRule = async (data: LegaltechRuleCreate): Promise<LegaltechRule> => {
  const res = await fetch(BASE(), {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `新增規則失敗: ${res.status}`);
  }
  return res.json();
};

/** 更新單條規則 */
export const updateLegaltechRule = async (
  id: number,
  data: LegaltechRuleUpdate
): Promise<LegaltechRule> => {
  const res = await fetch(`${BASE()}/${id}`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `更新規則失敗: ${res.status}`);
  }
  return res.json();
};

/** 刪除規則（軟刪除） */
export const deleteLegaltechRule = async (id: number): Promise<void> => {
  const res = await fetch(`${BASE()}/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `刪除規則失敗: ${res.status}`);
  }
};

/** 批量上傳 CSV */
export const uploadLegaltechRulesCsv = async (
  file: File,
  mode: 'append' | 'replace' = 'append'
): Promise<CsvUploadResult> => {
  const form = new FormData();
  form.append('file', file);

  const url = new URL(`${BASE()}/upload-csv`, window.location.origin);
  url.searchParams.set('mode', mode);

  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `CSV 上傳失敗: ${res.status}`);
  }
  return res.json();
};

/** 下載 CSV 範本 */
export const downloadLegaltechCsvTemplate = async (): Promise<void> => {
  const res = await fetch(`${BASE()}/template`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`下載範本失敗: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'legaltech_rules_template.csv';
  a.click();
  URL.revokeObjectURL(url);
};

/** 取得所有合約類型清單 */
export const listContractTypes = async (): Promise<string[]> => {
  const res = await fetch(`${BASE()}/contract-types`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`載入合約類型失敗: ${res.status}`);
  return res.json();
};

/** 匯出規則 CSV */
export const exportLegaltechRulesCsv = async (includeInactive = false): Promise<void> => {
  const url = new URL(`${BASE()}/export-csv`, window.location.origin);
  if (includeInactive) url.searchParams.set('include_inactive', 'true');

  const res = await fetch(url.toString(), { headers: authHeaders() });
  if (!res.ok) throw new Error(`匯出 CSV 失敗: ${res.status}`);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = 'legaltech_rules_export.csv';
  a.click();
  URL.revokeObjectURL(blobUrl);
};

/** 單條規則 AI 標準化 */
export const normalizeRule = async (id: number): Promise<NormalizeResponse> => {
  const res = await fetch(`${BASE()}/${id}/normalize`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `標準化失敗: ${res.status}`);
  }
  return res.json();
};

/** 批次 AI 標準化 */
export const normalizeBatch = async (): Promise<BatchNormalizeResponse> => {
  const res = await fetch(`${BASE()}/normalize-batch`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `批次標準化失敗: ${res.status}`);
  }
  return res.json();
};
